import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useEditorStore, useEditorStoreApi } from "../store/editorStoreSelectors";
import { MIN_ZOOM, MAX_ZOOM } from "../store/useEditorStore";
import { useInkSpritesSetting, setInkSpritesEnabled } from "../hooks/useInkSpritesSetting";

// Breathing room between the page and the edge of the desk, in screen px
const DESK_PADDING = 40;
const ZOOM_STEPS = [0.25, 0.33, 0.5, 0.67, 0.75, 0.9, 1, 1.25, 1.5, 2, 3, 4];

const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
};

/**
 * The desk the page sits on: a scrollable work surface that fits the page to
 * the window by default, zooms around the cursor (⌘/Ctrl-scroll, pinch,
 * ⌘+ / ⌘−; ⌘0 or Shift+1 fits, Shift+0 is 100%), and pans with Space-drag or the middle mouse button.
 *
 * Children are rendered at true page size; the viewport scales them.
 */
const CanvasViewport = ({ pageWidth, pageHeight, background, darkMode, overlay, onDeskClick, children }) => {
  const storeApi = useEditorStoreApi();
  const zoom = useEditorStore((st) => st.zoom);
  const zoomMode = useEditorStore((st) => st.zoomMode);
  const notice = useEditorStore((st) => st.notice);
  const spritesOn = useInkSpritesSetting() !== "off";

  // Desk notes fade away on their own unless they need attention
  useEffect(() => {
    if (!notice || notice.sticky) return undefined;
    const timer = window.setTimeout(() => storeApi.getState().clearNotice(), 3800);
    return () => window.clearTimeout(timer);
  }, [notice, storeApi]);

  const surfaceRef = useRef(null);
  const stageRef = useRef(null);
  const anchorRef = useRef(null);
  const justPannedRef = useRef(false);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [panning, setPanning] = useState(false);

  // ─── Fit to the desk ────────────────────────────────────────────────────
  const computeFitZoom = useCallback(() => {
    const surface = surfaceRef.current;
    if (!surface || !pageWidth || !pageHeight) return null;
    // A pixel of slack: a page that fits exactly can round into overflow,
    // show a scrollbar, shrink the desk, re-fit smaller, lose the scrollbar…
    const availableW = surface.clientWidth - DESK_PADDING * 2 - 1;
    const availableH = surface.clientHeight - DESK_PADDING * 2 - 1;
    if (!(availableW > 0) || !(availableH > 0)) return null;
    const fit = Math.min(availableW / pageWidth, availableH / pageHeight);
    return Number.isFinite(fit) ? clampZoom(fit) : null;
  }, [pageWidth, pageHeight]);

  const fitToDesk = useCallback(() => {
    const fit = computeFitZoom();
    if (fit != null) storeApi.getState().setZoom(fit, "fit");
  }, [computeFitZoom, storeApi]);

  useLayoutEffect(() => {
    if (zoomMode !== "fit") return undefined;
    fitToDesk();
    const surface = surfaceRef.current;
    if (!surface || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => fitToDesk());
    observer.observe(surface);
    return () => observer.disconnect();
  }, [zoomMode, fitToDesk]);

  // ─── Zoom around a point ────────────────────────────────────────────────
  // Remember which page point sits under the cursor, change zoom, then scroll
  // so that same point is back under the cursor.
  const zoomAt = useCallback((nextZoom, clientX, clientY) => {
    const stage = stageRef.current;
    const surface = surfaceRef.current;
    if (!stage || !surface) return;
    const current = storeApi.getState().zoom;
    const target = clampZoom(nextZoom);
    if (Math.abs(target - current) < 0.0001) return;
    const surfaceRect = surface.getBoundingClientRect();
    const cx = clientX ?? surfaceRect.left + surfaceRect.width / 2;
    const cy = clientY ?? surfaceRect.top + surfaceRect.height / 2;
    const stageRect = stage.getBoundingClientRect();
    anchorRef.current = {
      cx,
      cy,
      px: (cx - stageRect.left) / current,
      py: (cy - stageRect.top) / current,
    };
    storeApi.getState().setZoom(target, "manual");
  }, [storeApi]);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const stage = stageRef.current;
    const surface = surfaceRef.current;
    anchorRef.current = null;
    if (!anchor || !stage || !surface) return;
    const rect = stage.getBoundingClientRect();
    surface.scrollLeft += rect.left + anchor.px * zoom - anchor.cx;
    surface.scrollTop += rect.top + anchor.py * zoom - anchor.cy;
  }, [zoom]);

  const stepZoom = useCallback((direction) => {
    const current = storeApi.getState().zoom;
    const next = direction > 0
      ? ZOOM_STEPS.find((z) => z > current * 1.01) ?? MAX_ZOOM
      : [...ZOOM_STEPS].reverse().find((z) => z < current * 0.99) ?? MIN_ZOOM;
    zoomAt(next);
  }, [storeApi, zoomAt]);

  // ⌘/Ctrl + wheel (and trackpad pinch in Chrome/Firefox) zooms; needs a
  // non-passive listener so the browser doesn't zoom the whole page.
  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return undefined;
    const handleWheel = (event) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      // Trackpad pinches send small deltas; a mouse-wheel notch sends ~100.
      // Cap each event so one notch is a gentle ~20% step, not a leap.
      const pixels = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
      const capped = Math.max(-20, Math.min(20, pixels));
      const factor = Math.exp(-capped * 0.01);
      zoomAt(storeApi.getState().zoom * factor, event.clientX, event.clientY);
    };
    // Safari trackpad pinch
    let gestureBase = 1;
    const handleGestureStart = (event) => {
      event.preventDefault();
      gestureBase = storeApi.getState().zoom;
    };
    const handleGestureChange = (event) => {
      event.preventDefault();
      zoomAt(gestureBase * event.scale, event.clientX, event.clientY);
    };
    surface.addEventListener("wheel", handleWheel, { passive: false });
    surface.addEventListener("gesturestart", handleGestureStart);
    surface.addEventListener("gesturechange", handleGestureChange);
    return () => {
      surface.removeEventListener("wheel", handleWheel);
      surface.removeEventListener("gesturestart", handleGestureStart);
      surface.removeEventListener("gesturechange", handleGestureChange);
    };
  }, [storeApi, zoomAt]);

  // ─── Keyboard: zoom shortcuts and Space-to-pan ──────────────────────────
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isTypingTarget(event.target)) return;
      const mod = event.metaKey || event.ctrlKey;
      if (mod && (event.key === "=" || event.key === "+")) {
        event.preventDefault();
        stepZoom(1);
      } else if (mod && event.key === "-") {
        event.preventDefault();
        stepZoom(-1);
      } else if (mod && event.key === "0") {
        event.preventDefault();
        fitToDesk();
      } else if (event.shiftKey && !mod && event.code === "Digit1") {
        event.preventDefault();
        fitToDesk();
      } else if (event.shiftKey && !mod && event.code === "Digit0") {
        event.preventDefault();
        zoomAt(1);
      } else if (event.code === "Space" && !mod) {
        if (event.target.tagName === "BUTTON") return;
        event.preventDefault();
        setSpaceHeld(true);
      }
    };
    const handleKeyUp = (event) => {
      if (event.code === "Space") setSpaceHeld(false);
    };
    const handleBlur = () => setSpaceHeld(false);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, [stepZoom, fitToDesk, zoomAt]);

  // ─── Panning: Space-drag or middle mouse button ─────────────────────────
  const handlePointerDownCapture = (event) => {
    const middleButton = event.pointerType === "mouse" && event.button === 1;
    if (!spaceHeld && !middleButton) return;
    const surface = surfaceRef.current;
    if (!surface) return;
    // The desk takes this pointer; nothing on the page should react to it
    event.preventDefault();
    event.stopPropagation();
    const start = { x: event.clientX, y: event.clientY, left: surface.scrollLeft, top: surface.scrollTop };
    let moved = false;
    setPanning(true);

    const handleMove = (moveEvent) => {
      if (moveEvent.pointerId !== event.pointerId) return;
      const dx = moveEvent.clientX - start.x;
      const dy = moveEvent.clientY - start.y;
      if (Math.abs(dx) + Math.abs(dy) > 2) moved = true;
      surface.scrollLeft = start.left - dx;
      surface.scrollTop = start.top - dy;
    };
    const handleUp = (upEvent) => {
      if (upEvent.pointerId !== event.pointerId) return;
      justPannedRef.current = moved;
      setPanning(false);
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
  };

  // Swallow the click that ends a pan so it doesn't select a panel
  const handleClickCapture = (event) => {
    if (!justPannedRef.current) return;
    justPannedRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  const ink = darkMode ? "#e8e2d5" : "#1a1a1a";
  const paper = darkMode ? "#2a251e" : "#fffdf6";
  const edge = darkMode ? "#4a4238" : "#1a1a1a";
  const zoomButton = {
    height: 26,
    minWidth: 26,
    padding: "0 6px",
    border: "none",
    background: "transparent",
    color: ink,
    cursor: "pointer",
    fontFamily: "'Georgia', serif",
    fontSize: 13,
  };

  return (
    <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
      <div
        ref={surfaceRef}
        data-testid="canvas-desk"
        style={{
          position: "absolute",
          inset: 0,
          overflow: "auto",
          background,
          cursor: panning ? "grabbing" : spaceHeld ? "grab" : undefined,
        }}
        onPointerDownCapture={handlePointerDownCapture}
        onClickCapture={handleClickCapture}
        onClick={(event) => {
          if (event.target === event.currentTarget || event.target.dataset?.desk) onDeskClick?.();
        }}
      >
        <div
          data-desk="1"
          style={{
            display: "flex",
            boxSizing: "border-box",
            minWidth: "100%",
            minHeight: "100%",
            width: "max-content",
            padding: DESK_PADDING,
          }}
        >
          <div
            ref={stageRef}
            style={{
              position: "relative",
              flex: "none",
              margin: "auto",
              width: pageWidth * zoom,
              height: pageHeight * zoom,
              // While panning, the page shouldn't show grab cursors or hovers
              pointerEvents: spaceHeld || panning ? "none" : undefined,
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                transform: `scale(${zoom})`,
                transformOrigin: "0 0",
              }}
            >
              {children}
            </div>
          </div>
        </div>
      </div>

      {overlay}

      {notice && (
        <div
          key={notice.at}
          role="status"
          className="striply-place"
          style={{
            position: "absolute",
            left: 16,
            bottom: 16,
            zIndex: 85,
            maxWidth: 360,
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            padding: "9px 12px",
            background: "#f6ecd0",
            color: "#1a1a1a",
            border: "2px solid #1a1a1a",
            borderLeft: "6px solid #b42318",
            boxShadow: "4px 4px 0 rgba(0, 0, 0, 0.25)",
            transform: "rotate(-0.4deg)",
            fontFamily: "'Georgia', serif",
            fontSize: 12,
            lineHeight: 1.45,
          }}
        >
          <span style={{ flex: 1 }}>{notice.text}</span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => storeApi.getState().clearNotice()}
            style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 15, lineHeight: 1, padding: 0, color: "#1a1a1a" }}
          >
            ×
          </button>
        </div>
      )}

      <div
        role="group"
        aria-label="Zoom"
        style={{
          position: "absolute",
          right: 16,
          bottom: 16,
          zIndex: 80,
          display: "flex",
          alignItems: "center",
          gap: 2,
          padding: 3,
          background: paper,
          border: `2px solid ${edge}`,
          boxShadow: "3px 3px 0 rgba(0, 0, 0, 0.22)",
        }}
      >
        <button type="button" style={zoomButton} onClick={() => stepZoom(-1)}
          title="Zoom out (⌘−)" aria-label="Zoom out">−</button>
        <button type="button" style={{ ...zoomButton, minWidth: 48, fontVariantNumeric: "tabular-nums" }}
          onClick={() => zoomAt(1)} title="Actual size (Shift+0)" aria-label="Zoom to actual size">
          {Math.round(zoom * 100)}%
        </button>
        <button type="button" style={zoomButton} onClick={() => stepZoom(1)}
          title="Zoom in (⌘+)" aria-label="Zoom in">+</button>
        <button
          type="button"
          onClick={fitToDesk}
          title="Fit page to window (Shift+1)"
          aria-pressed={zoomMode === "fit"}
          style={{
            ...zoomButton,
            marginLeft: 2,
            fontSize: 11,
            letterSpacing: 1,
            textTransform: "uppercase",
            border: `1px solid ${zoomMode === "fit" ? "#1a1a1a" : "transparent"}`,
            background: zoomMode === "fit" ? "#ffe600" : "transparent",
            color: zoomMode === "fit" ? "#1a1a1a" : ink,
          }}
        >
          Fit
        </button>
        <span aria-hidden="true" style={{ width: 1, alignSelf: "stretch", margin: "3px 2px", background: edge, opacity: 0.4 }} />
        <button
          type="button"
          onClick={() => setInkSpritesEnabled(!spritesOn)}
          aria-pressed={spritesOn}
          aria-label="Ink sprites"
          title={spritesOn ? "Ink sprites live in the gutters when you pause — click to shoo them away for good" : "Let the ink sprites back into the gutters"}
          style={{ ...zoomButton, display: "flex", alignItems: "center", justifyContent: "center", opacity: spritesOn ? 1 : 0.45 }}
        >
          <svg width="16" height="16" viewBox="-8 -8 16 16" aria-hidden="true">
            <circle r="5.6" fill={ink} />
            <path d="M -6.6,-1 L -5.6,0 L -6.8,1.2 M 6.6,-1 L 5.6,0 L 6.8,1.2 M -1,-6.6 L 0,-5.6 L 1.2,-6.8 M -1,6.6 L 0,5.6 L 1.2,6.8" stroke={ink} strokeWidth="1" fill="none" />
            <circle cx="-2" cy="-0.7" r="1.7" fill={paper} />
            <circle cx="2" cy="-0.7" r="1.7" fill={paper} />
            <circle cx="-1.6" cy="-0.5" r="0.75" fill={ink} />
            <circle cx="2.4" cy="-0.5" r="0.75" fill={ink} />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default CanvasViewport;
